import { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';

export interface CategoryItem {
  id: string; name: string; description: string; businesses: number;
  enabled: boolean; consultation_enabled: boolean; color: string;
}

export function useCategories() {
  const [items, setItems] = useState<CategoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.CATEGORIES);
      const data = res.data?.data ?? res.data;
      setItems(data.items);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load categories.');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Enable/disable a category. Optimistically updates, reverts on failure. */
  const toggle = useCallback(async (id: string, enabled: boolean): Promise<boolean> => {
    setItems((prev) => prev.map((c) => (c.id === id ? { ...c, enabled } : c)));
    try {
      await axiosInstance.patch(`${API_ENDPOINTS.CATEGORIES}/${id}`, { is_active: enabled });
      return true;
    } catch {
      setItems((prev) => prev.map((c) => (c.id === id ? { ...c, enabled: !enabled } : c)));
      return false;
    }
  }, []);

  /** Enable/disable the Consultation option for a category. Optimistic, reverts on failure. */
  const toggleConsultation = useCallback(async (id: string, on: boolean): Promise<boolean> => {
    setItems((prev) => prev.map((c) => (c.id === id ? { ...c, consultation_enabled: on } : c)));
    try {
      await axiosInstance.patch(`${API_ENDPOINTS.CATEGORIES}/${id}`, { consultation_enabled: on });
      return true;
    } catch {
      setItems((prev) => prev.map((c) => (c.id === id ? { ...c, consultation_enabled: !on } : c)));
      return false;
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  return { items, loading, error, reload: load, toggle, toggleConsultation };
}
